import { useState, useEffect, useMemo, useCallback } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import {
    getSession,
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    updateSessionCampus,
} from '../../../services/session.js';
import { apiMqtt } from '../../../services/api.js';
import { iAmHigherAuthority } from '../../../libs.jsx';
import {
    fetchProfiles,
    fetchDataTypes,
    addProfile,
    editProfile,
    deleteProfiles,
} from './api/palPendantApi.js';

import ProfileTable from './components/ProfileTable.jsx';
import ProfileDrawer from './components/ProfileDrawer.jsx';

import './palpendant-settings-page.css';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const PalPendantSettingsPage = () => {
    // -- Session --
    const [campusId, setCampusId] = useState(() => getCampusId());
    const session = useMemo(() => getSession(), []);

    const campusList = useMemo(() => {
        return getCampusList() || [];
    }, []);

    const campusName = useMemo(() => {
        const match = campusList.find((c) => c.campus_id === campusId);
        return match?.campus_name || `Campus ${campusId}`;
    }, [campusId, campusList]);

    // Permissions
    const editRights = useMemo(() => iAmHigherAuthority(20, session?.role_weight), [session]);
    const adminRights = useMemo(() => iAmHigherAuthority(99, session?.role_weight), [session]);

    // -- Data stores --
    const [profileData, setProfileData] = useState([]);
    const [dataTypes, setDataTypes] = useState([]);

    // -- Loading / Saving --
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [restoring, setRestoring] = useState(false);

    // -- Drawer --
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);

    // -- Delete Dialog --
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteIds, setDeleteIds] = useState([]);

    // -- Toast --
    const [toast, setToast] = useState(null);
    const showToast = useCallback((message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    // ======================================================================
    // Data loading
    // ======================================================================
    const loadData = useCallback(async (cId) => {
        setLoading(true);
        try {
            const [profilesRes, dataTypesRes] = await Promise.all([
                fetchProfiles(cId),
                fetchDataTypes(),
            ]);

            setProfileData(profilesRes || []);
            setDataTypes(dataTypesRes || []);
        } catch (err) {
            console.error('[PalPendantSettingsPage] Load error:', err);
            showToast('Failed to load pendant profile data.', 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    // Initial load + reload on campus switch
    useEffect(() => {
        loadData(campusId);
    }, [campusId, loadData]);

    // ======================================================================
    // Campus switch
    // ======================================================================
    const handleCampusChange = (newId) => {
        setCampusId(newId);
        updateSessionCampus(newId);

        const oldIsCloud = session?.campusData?.[campusId?.toString()]?.is_cloud;
        const newIsCloud = session?.campusData?.[newId?.toString()]?.is_cloud;
        if (oldIsCloud !== newIsCloud) {
            window.location.reload();
        }
    };

    // ======================================================================
    // MQTT Operations — Sync Local & Restore Data
    // ======================================================================
    const handleSyncLocal = async () => {
        setSyncing(true);
        try {
            const [ok] = await apiMqtt({
                operation: 'reboot-server',
                campusId,
                clientId: getSessionId(),
            });
            if (ok) {
                showToast('Sync triggered. Please allow up to 3 minutes for changes to take effect.');
            } else {
                showToast('Error syncing. Contact support if the error persists.', 'error');
            }
        } catch {
            showToast('Error syncing. Contact support if the error persists.', 'error');
        } finally {
            setSyncing(false);
        }
    };

    const handleRestoreData = async () => {
        setRestoring(true);
        try {
            const [ok] = await apiMqtt({
                operation: 'restore-server',
                campusId,
                clientId: getSessionId(),
            });
            if (ok) {
                showToast('Restore triggered. Please allow up to 3 minutes for changes to take effect.');
            } else {
                showToast('Error restoring. Contact support if the error persists.', 'error');
            }
        } catch {
            showToast('Error restoring. Contact support if the error persists.', 'error');
        } finally {
            setRestoring(false);
        }
    };

    // ======================================================================
    // CRUD meta
    // ======================================================================
    const meta = useMemo(() => ({
        campusId,
        userId: getUserId(),
        clientId: getSessionId(),
    }), [campusId]);

    // ======================================================================
    // Add Profile
    // ======================================================================
    const openAddDrawer = () => {
        setEditTarget(null);
        setDrawerOpen(true);
    };

    // ======================================================================
    // Edit Profile
    // ======================================================================
    const handleEditProfile = (profile) => {
        setEditTarget(profile);
        setDrawerOpen(true);
    };

    // ======================================================================
    // Save Profile (handles both add & edit)
    // ======================================================================
    const handleSaveProfile = async ({ profileName, modes, editTarget: et }) => {
        setSaving(true);
        try {
            if (et) {
                // EDIT — build payload + oldPayload
                const payload = [{
                    dsp_profile_id: et.dsp_profile_id,
                    profile_name: profileName,
                    modes: modes.map((m) => ({
                        ...m,
                        modified_by: session?.user || getUserId(),
                    })),
                }];

                // Old payload for audit
                const oldPayload = [
                    { dsp_profile_id: et.dsp_profile_id, profile_name: et.profile_name },
                    ...(et.modes || []),
                ];

                const [ok] = await editProfile(payload, oldPayload, meta);
                if (ok) {
                    setDrawerOpen(false);
                    setEditTarget(null);
                    showToast(`Profile "${profileName}" has been updated.`);
                    await loadData(campusId);
                } else {
                    showToast('Failed to update profile.', 'error');
                }
            } else {
                // ADD
                const payload = [{
                    profile_name: profileName,
                    modes,
                }];

                const [ok] = await addProfile(payload, meta);
                if (ok) {
                    setDrawerOpen(false);
                    showToast(`Profile "${profileName}" has been created.`);
                    await loadData(campusId);
                } else {
                    showToast('Failed to create profile.', 'error');
                }
            }
        } catch {
            showToast(`Failed to ${et ? 'update' : 'create'} profile.`, 'error');
        } finally {
            setSaving(false);
        }
    };

    // ======================================================================
    // Delete Profiles
    // ======================================================================
    const handleRequestDelete = (ids) => {
        setDeleteIds(ids);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        setDeleteDialogOpen(false);
        setSaving(true);
        try {
            const payload = deleteIds.map((id) => ({ dsp_profile_id: id }));
            const [ok] = await deleteProfiles(payload, meta);
            if (ok) {
                const count = deleteIds.length;
                showToast(`${count} profile${count !== 1 ? 's' : ''} deleted.`);
                setDeleteIds([]);
                await loadData(campusId);
            } else {
                showToast('Failed to delete profile(s).', 'error');
            }
        } catch {
            showToast('Failed to delete profile(s).', 'error');
        } finally {
            setSaving(false);
        }
    };

    // ======================================================================
    // Render helpers
    // ======================================================================
    const renderContent = () => {
        if (loading) {
            return (
                <div className="pp-section">
                    <div className="pp-section__body">
                        <div className="ss-skeleton" style={{ width: '60%', height: '16px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '100%', height: '44px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '80%', height: '16px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '100%', height: '44px' }} />
                    </div>
                </div>
            );
        }

        if (profileData.length === 0) {
            return (
                <div className="ss-empty">
                    <svg className="ss-empty__icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                    </svg>
                    <p className="ss-empty__text">No pendant display profiles configured for this campus.</p>
                    <button className="dp-btn dp-btn--primary" onClick={openAddDrawer}>
                        <i className="fe fe-plus" style={{ marginRight: '0.5rem' }} />
                        New Profile
                    </button>
                </div>
            );
        }

        return (
            <ProfileTable
                profiles={profileData}
                onEdit={handleEditProfile}
                onDelete={handleRequestDelete}
                onBulkDelete={handleRequestDelete}
                saving={saving}
            />
        );
    };

    // ======================================================================
    // Delete dialog text
    // ======================================================================
    const deleteCount = deleteIds.length;
    const deleteProfileNames = deleteIds
        .map((id) => profileData.find((p) => p.dsp_profile_id === id)?.profile_name)
        .filter(Boolean);

    // ======================================================================
    // Render
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ss-page">
                    {/* Toast notification */}
                    {toast ? (
                        <div className={`dp-toast dp-toast--${toast.type}`}>
                            <i className={`fe ${toast.type === 'success' ? 'fe-check-circle' : 'fe-alert-circle'}`} />
                            {toast.message}
                            <button className="dp-toast__close" onClick={() => setToast(null)}>
                                <i className="fe fe-x" />
                            </button>
                        </div>
                    ) : null}

                    {/* Page header */}
                    <div className="dp-page-header">
                        <div className="dp-page-header__inner">
                            <div className="dp-breadcrumb-row">
                                <nav className="dp-breadcrumb">
                                    <span className="dp-breadcrumb__text">Settings</span>
                                    <span className="dp-breadcrumb__separator">/</span>
                                    <span className="dp-breadcrumb__current">PalPendant Display Profiles</span>
                                </nav>
                                <div className="dp-campus-row">
                                    {/* MQTT action buttons */}
                                    {editRights ? (
                                        <button
                                            className="ap-btn ap-btn--secondary"
                                            onClick={handleSyncLocal}
                                            disabled={syncing}
                                            title="Syncs local server with cloud data, will cause the server to go offline momentarily during the process"
                                        >
                                            {syncing ? 'Syncing...' : 'Sync Local'}
                                        </button>
                                    ) : null}
                                    {adminRights ? (
                                        <button
                                            className="ap-btn ap-btn--secondary"
                                            onClick={handleRestoreData}
                                            disabled={restoring}
                                            title="Restore data performs the sync local reboot, however deletes the database file triggering a re-create."
                                        >
                                            {restoring ? 'Restoring...' : 'Restore Data'}
                                        </button>
                                    ) : null}

                                    {/* Campus selector */}
                                    <label htmlFor="pp-campus-selector" className="dp-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select
                                            id="pp-campus-selector"
                                            value={campusId}
                                            onChange={(e) => handleCampusChange(parseInt(e.target.value, 10))}
                                            className="dp-campus-select"
                                        >
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>
                                                    {c.campus_name}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="dp-campus-name">{campusName}</span>
                                    )}
                                </div>
                            </div>

                            {/* Page-level action button */}
                            {profileData.length > 0 ? (
                                <div className="lc-page-actions">
                                    <button className="dp-btn dp-btn--primary" onClick={openAddDrawer}>
                                        <i className="fe fe-plus" style={{ marginRight: '0.5rem' }} />
                                        New Profile
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    {/* Content — table or empty state */}
                    <div className="pp-tab-content">
                        {renderContent()}
                    </div>

                    {/* Profile Drawer (add/edit) */}
                    <ProfileDrawer
                        open={drawerOpen}
                        onClose={() => { setDrawerOpen(false); setEditTarget(null); }}
                        onSave={handleSaveProfile}
                        saving={saving}
                        editTarget={editTarget}
                        dataTypes={dataTypes}
                    />

                    {/* Delete Confirmation Dialog */}
                    {deleteDialogOpen ? (
                        <div className="pp-dialog-backdrop" onClick={() => setDeleteDialogOpen(false)}>
                            <div className="pp-dialog" onClick={(e) => e.stopPropagation()}>
                                <div className="pp-dialog__header">
                                    <h3 className="pp-dialog__title">Delete Profile{deleteCount !== 1 ? 's' : ''}</h3>
                                    <button className="dp-drawer__close" onClick={() => setDeleteDialogOpen(false)}>
                                        <i className="fe fe-x" />
                                    </button>
                                </div>
                                <div className="pp-dialog__body">
                                    <p className="pp-dialog__text">
                                        {deleteCount === 1 ? (
                                            <>Are you sure you want to delete <strong>{deleteProfileNames[0]}</strong>? This action cannot be undone.</>
                                        ) : (
                                            <>Are you sure you want to delete <strong>{deleteCount} profiles</strong>? This action cannot be undone.</>
                                        )}
                                    </p>
                                </div>
                                <div className="pp-dialog__footer">
                                    <button className="dp-btn dp-btn--cancel" onClick={() => setDeleteDialogOpen(false)}>
                                        Cancel
                                    </button>
                                    <button className="dp-btn dp-btn--danger" disabled={saving} onClick={handleConfirmDelete}>
                                        {saving ? 'Deleting...' : `Delete${deleteCount !== 1 ? ` (${deleteCount})` : ''}`}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </>
    );
};

export default PalPendantSettingsPage;
